import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import DatePicker from "react-datepicker";
import { FaMapMarkerAlt } from "react-icons/fa";
import { addDays } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";
import styles from "../styles/ads-landing.module.scss";
import logo from "../assets/electrician-logo2.png";
import SummaryModal from "../components/modals/summary";
import { services } from "../utils/objects/services-object";
import {
  createTimeSlots,
  formatEmailDate,
  formatEmailStartTime,
  getStartAndEndTimes,
  isWeekday,
} from "../utils/funcs/google-calendar";
import { getLatLonFromPostcode } from "../utils/funcs/lat-ln";
import { isInRadius } from "../utils/funcs/is-in-radius";
import { validateEmail } from "../utils/funcs/validate-email";
import { validateMobile } from "../utils/funcs/validate-mobile";
import { capitaliseAllChars } from "../utils/funcs/capitalise-all-chars";
import { capitaliseFirstLetter } from "../utils/funcs/capitalise-first-letter";
import { formatServicePrice } from "../utils/funcs/pricing";

const standardEicrService = services.find(
  (service) => service.slug === "EICR-certificate",
);

const guaranteedPassService = services.find(
  (service) => service.slug === "guaranteed-pass-EICR",
);

const quickBookingServices = [
  {
    key: "guaranteed-pass",
    title: guaranteedPassService?.title ?? "Guaranteed EICR Pass Certificate",
    shortTitle: "Guaranteed pass EICR",
    productName:
      guaranteedPassService?.title ?? "Guaranteed EICR Pass Certificate",
    description:
      "Fixed-price EICR at £250 with minor remedial works included to help you reach a satisfactory pass result.",
    basePrice: guaranteedPassService?.basePrice ?? 250,
    pricePrefix: "Fixed price",
  },
  {
    key: "standard-eicr",
    title: standardEicrService?.title ?? "EICR Certificate",
    shortTitle: "Standard EICR",
    productName: "EICR Certificate",
    description:
      "Standard landlord EICR inspection with clear reporting and next-step recommendations.",
    basePrice: standardEicrService?.basePrice ?? 79,
    pricePrefix: "From",
  },
] as const;

type QuickBookingServiceKey = (typeof quickBookingServices)[number]["key"];

const faqs = [
  {
    question: "What is an EICR?",
    answer:
      "An Electrical Installation Condition Report is a formal assessment of the fixed electrical installation in a property, highlighting defects, damage or safety concerns.",
  },
  {
    question: "How often do landlords need one?",
    answer:
      "For most private rented homes in England, the installation should be inspected at least every five years, or sooner if the report requires it. Confirm the rules that apply to your property.",
  },
  {
    question: "How long does the inspection take?",
    answer:
      "Timing depends on the property size, number of circuits and accessibility. Teck Engineers will confirm expectations for your particular booking.",
  },
  {
    question: "What if remedial work is needed?",
    answer:
      "Your report will identify observations using standard classification codes. You can then discuss any necessary remedial work and next steps.",
  },
];

const getDefaultBookingDate = () => {
  const today = new Date();
  const nextDay = new Date(today);
  nextDay.setDate(today.getDate() + 1);

  if (nextDay.getDay() === 0) {
    nextDay.setDate(nextDay.getDate() + 1);
  }

  return nextDay;
};

const calculateDomesticEicrPrice = (bedrooms: number, fuseboxes: number) => {
  const basePrice = standardEicrService?.basePrice ?? 79;
  const fuseboxPrice = Math.max(fuseboxes - 1, 0) * 45;

  if (bedrooms === 1) return basePrice + fuseboxPrice;
  if (bedrooms === 2) return 80 + fuseboxPrice;
  if (bedrooms === 3) return 82 + fuseboxPrice;
  if (bedrooms === 4) return 90 + fuseboxPrice;
  if (bedrooms === 5) return 99 + fuseboxPrice;

  const extraRooms = bedrooms - 5;
  return 90 + extraRooms * 10 + fuseboxPrice;
};

const calculateGuaranteedPassPrice = () =>
  guaranteedPassService?.basePrice ?? 250;

const getQuickBookingService = (serviceKey: QuickBookingServiceKey) =>
  quickBookingServices.find((service) => service.key === serviceKey) ??
  quickBookingServices[1];

type AdsLandingBookingItem = {
  bookingID: string;
  productName: string;
  price: number;
  productInfo: string;
  customerName: string;
  customerEmail: string;
  customerNumber: string;
  start: Date | string;
  end: Date | string;
  date: Date;
  emailDate: string;
  emailStartTime: string;
  emailEndTime?: string;
  access: string;
  address: {
    line1: string;
    line2: string;
    postTown: string;
    postcode: string;
  };
  finalPrice: number;
  accessName: string;
  accessEmail: string;
  accessNumber: string;
};

export default function AdsLandingPage() {
  const defaultBookingDate = useMemo(() => getDefaultBookingDate(), []);
  const timeSlots = useMemo(() => createTimeSlots(), []);
  const secureBookingRef = useRef<HTMLElement | null>(null);
  const [selectedServiceKey, setSelectedServiceKey] =
    useState<QuickBookingServiceKey>("standard-eicr");
  const selectedService = getQuickBookingService(selectedServiceKey);

  const [bedrooms, setBedrooms] = useState(1);
  const [fuseboxes, setFuseboxes] = useState(1);
  const [preferredDate, setPreferredDate] = useState<Date | null>(
    defaultBookingDate,
  );
  const [selectedTime, setSelectedTime] = useState("08:00");
  const [showSecureBooking, setShowSecureBooking] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [checkingCoverage, setCheckingCoverage] = useState(false);
  const [availabilityMessage, setAvailabilityMessage] = useState(
    "Enter a London postcode to confirm coverage",
  );
  const [error, setError] = useState("");
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState("");
  const [accessProvidedByOther, setAccessProvidedByOther] = useState(false);
  const [addressLookupQuery, setAddressLookupQuery] = useState("");
  const [addressLookupResults, setAddressLookupResults] = useState<any[]>([]);
  const [item, setItem] = useState<AdsLandingBookingItem>({
    bookingID: " ",
    productName: selectedService.productName,
    price: selectedService.basePrice,
    productInfo: "Domestic: No. of bedrooms: 1, No. of fuseboxes: 1",
    customerName: "",
    customerEmail: "",
    customerNumber: "",
    start: "",
    end: "",
    date: defaultBookingDate,
    emailDate: formatEmailDate(defaultBookingDate),
    emailStartTime: "8:00 AM",
    access: "Access provided by: customer",
    address: { line1: "", line2: " ", postTown: "", postcode: "" },
    finalPrice: selectedService.basePrice,
    accessName: "",
    accessEmail: "",
    accessNumber: "",
  });

  useEffect(() => {
    window.fbq?.("track", "PageView");
  }, []);

  useEffect(() => {
    const nextPrice =
      selectedServiceKey === "guaranteed-pass"
        ? calculateGuaranteedPassPrice()
        : calculateDomesticEicrPrice(bedrooms, fuseboxes);

    setItem((prevItem) => ({
      ...prevItem,
      productName: selectedService.productName,
      price: nextPrice,
      finalPrice: nextPrice,
      productInfo:
        selectedServiceKey === "guaranteed-pass"
          ? `Domestic: No. of bedrooms: ${bedrooms}, No. of fuseboxes: ${fuseboxes}`
          : `Domestic: No. of bedrooms: ${bedrooms}, No. of fuseboxes: ${fuseboxes}`,
    }));
  }, [bedrooms, fuseboxes, selectedService, selectedServiceKey]);

  useEffect(() => {
    if (!preferredDate) {
      return;
    }

    const { start, end } = getStartAndEndTimes(selectedTime, preferredDate);

    setItem((prevItem) => ({
      ...prevItem,
      date: preferredDate,
      emailDate: formatEmailDate(preferredDate),
      emailStartTime: formatEmailStartTime(start),
      emailEndTime: formatEmailStartTime(end),
      start,
      end,
    }));
  }, [preferredDate, selectedTime]);

  useEffect(() => {
    const fetchMaintenanceMode = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL || "http://localhost:5000"}/maintenance-mode`,
        );

        if (!response.ok) {
          return;
        }

        const data = await response.json();
        setMaintenanceMode(data.maintenanceMode);
        setMaintenanceMessage(data.maintenanceMessage);
      } catch (fetchError) {
        console.error("Error fetching maintenance mode:", fetchError);
      }
    };

    fetchMaintenanceMode();
  }, []);

  const findPostcode = async (postcode: string) => {
    const query = postcode.trim();

    if (!query) {
      setAddressLookupResults([]);
      return;
    }

    try {
      const response = await axios.get(
        `https://ws.postcoder.com/pcw/autocomplete/find?query=${encodeURIComponent(query)}&country=uk&apikey=PCWNU-UC9YJ-R24LJ-MCMT6`,
      );

      setAddressLookupResults(
        Array.isArray(response.data) ? response.data : [],
      );
    } catch (lookupError) {
      console.error("Address lookup failed:", lookupError);
      setAddressLookupResults([]);
    }
  };

  const handleAddressLookupSelect = async (result: any) => {
    if (!result) {
      return;
    }

    if (result.id) {
      const splitAddress = result.summaryline?.split(", ") ?? [];
      const selectedPostcode = splitAddress[splitAddress.length - 1] ?? "";
      setAddressLookupQuery(selectedPostcode);

      try {
        const response = await axios.get(
          `https://ws.postcoder.com/pcw/autocomplete/retrieve/?id=${result.id}&query=${encodeURIComponent(selectedPostcode)}&country=uk&apikey=PCWNU-UC9YJ-R24LJ-MCMT6`,
        );

        if (response.data.length === 1) {
          const selectedAddress = response.data[0];

          setAddressLookupResults([]);
          setItem((prevItem) => ({
            ...prevItem,
            address: {
              ...prevItem.address,
              line1: selectedAddress.subbuildingname
                ? selectedAddress.premise
                : `${selectedAddress.number} ${selectedAddress.street}`,
              line2:
                selectedAddress.dependentlocality ??
                selectedAddress.street ??
                " ",
              postTown: selectedAddress.posttown,
              postcode: selectedAddress.postcode,
            },
          }));

          await checkCoverage(selectedAddress.postcode);
          return;
        }

        if (response.data.length > 1) {
          setAddressLookupResults(response.data);
        }
      } catch (lookupError) {
        console.error("Address retrieve failed:", lookupError);
      }

      return;
    }

    setAddressLookupResults([]);
    setAddressLookupQuery(
      [result.summaryline, result.locationsummary].filter(Boolean).join(", "),
    );
    setItem((prevItem) => ({
      ...prevItem,
      address: {
        ...prevItem.address,
        line1: `${result.number} ${result.street}`,
        line2: result.dependentlocality || " ",
        postTown: result.posttown,
        postcode: result.postcode,
      },
    }));

    await checkCoverage(result.postcode);
  };

  const checkCoverage = async (postcodeOverride?: string) => {
    const cleanedPostcode = (postcodeOverride ?? item.address.postcode).trim();

    if (!cleanedPostcode) {
      setAvailabilityMessage("Enter a London postcode to confirm coverage");
      return false;
    }

    setCheckingCoverage(true);
    setAvailabilityMessage("Checking London coverage...");

    try {
      const data = await getLatLonFromPostcode(cleanedPostcode);

      if (!data) {
        setAvailabilityMessage(
          "We could not verify that postcode. Please check it and try again.",
        );
        return false;
      }

      const covered = isInRadius(data.adminDistrict, data.lat, data.lon);

      setAvailabilityMessage(
        covered
          ? "London coverage confirmed for this address."
          : "That postcode is outside our current London coverage area.",
      );

      return covered;
    } catch (coverageError) {
      console.error("Coverage check failed:", coverageError);
      setAvailabilityMessage(
        "We could not confirm coverage right now. Please try again.",
      );
      return false;
    } finally {
      setCheckingCoverage(false);
    }
  };

  const handleContinueToBooking = async () => {
    setError("");

    if (!preferredDate) {
      setError("Choose a preferred date before continuing.");
      return;
    }

    const covered = await checkCoverage();

    if (!covered) {
      setError(
        "We could not confirm this booking address is within our London coverage area.",
      );
      return;
    }

    setShowSecureBooking(true);
    window.requestAnimationFrame(() => {
      secureBookingRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  };

  const handleOpenSummary = async () => {
    setError("");

    const requiredFields = [
      item.customerName.trim(),
      item.customerEmail.trim(),
      item.customerNumber.trim(),
      item.address.line1.trim(),
      item.address.postTown.trim(),
      item.address.postcode.trim(),
    ];

    if (requiredFields.some((value) => !value)) {
      setError("Complete all required details before continuing to payment.");
      return;
    }

    if (accessProvidedByOther) {
      if (
        !item.accessName.trim() ||
        !item.accessEmail.trim() ||
        !item.accessNumber.trim()
      ) {
        setError("Enter the contact details for the person providing access.");
        return;
      }
    }

    const bookingEmailValid = await validateEmail(item.customerEmail);
    if (!bookingEmailValid) {
      setError("Enter a valid email address.");
      return;
    }

    const bookingMobileValid = await validateMobile(item.customerNumber);
    if (!bookingMobileValid) {
      setError("Enter a valid phone number.");
      return;
    }

    if (accessProvidedByOther) {
      const accessEmailValid = await validateEmail(item.accessEmail);
      if (!accessEmailValid) {
        setError("Enter a valid access contact email address.");
        return;
      }

      const accessMobileValid = await validateMobile(item.accessNumber);
      if (!accessMobileValid) {
        setError("Enter a valid access contact phone number.");
        return;
      }
    }

    const covered = await checkCoverage();
    if (!covered) {
      setError(
        "We could not confirm this booking address is within our London coverage area.",
      );
      return;
    }

    setShowSummary(true);
  };

  const appointmentTime = item.emailEndTime
    ? `${item.emailStartTime} to ${item.emailEndTime}`
    : item.emailStartTime;

  return (
    <div className={styles.page}>
    

      <header className={styles.mainHeader}>
        <div className={styles.headerInner}>
          <a
            href="#top"
            className={styles.headerBrand}
            aria-label="Teck Engineers home"
          >
            <img src={logo} alt="Teck Engineers" className={styles.logo} />
          </a>
          <div className={styles.headerActions}>
            <div className={styles.headerPhone}>
              <span>Questions? Speak to an electrician</span>
              <a href="tel:07496445148">07496 445 148</a>
            </div>
            <a href="#booking-card" className={styles.headerButton}>
              Check availability
            </a>
          </div>
        </div>
      </header>
  <div className={styles.announcementBar}>
        London landlords: stay compliant with a professional EICR
      </div>
      <section className={styles.hero} id="top">
        <div className={styles.heroContent}>
          <div className={styles.heroCopy}>
            <span className={styles.eyebrow}>
              EICR certificates across London
            </span>
            <h1>
              Get your rental property electrically certified—without the
              hassle.
            </h1>
            <p className={styles.heroLead}>
              A thorough electrical safety inspection, clear report and
              practical next steps from qualified professionals. Appointments
              from £79, or choose our guaranteed pass option from £250.
            </p>
            <div className={styles.heroBadges}>
              <span>
                <b>✓</b> Digital certificate included
              </span>
              <span>
                <b>✓</b> EICR available within 24-48 hours of the inspection
              </span>
              <span>
                <b>✓</b> Convenient morning or afternoon slots
              </span>
              <span>
                <b>✓</b> Straightforward pricing before you book
              </span>
            </div>
            <div className={styles.heroActions}>
              <a href="#booking-card" className={styles.primaryAction}>
                Check dates &amp; price <span aria-hidden="true">→</span>
              </a>
              <a href="tel:07496445148" className={styles.secondaryAction}>
                Or call 07496 445 148
              </a>
            </div>
            <p className={styles.smallPrint}>
              No obligation · Takes less than 60 seconds
            </p>
          </div>

          <aside className={styles.bookingCard} id="booking-card">
            <div className={styles.cardEyebrow}>Quick booking</div>
            <h2>Check your price &amp; availability</h2>
            <p className={styles.cardIntro}>
              Choose your service, then tell us a little about the property.
            </p>

            {maintenanceMode ? (
              <div className={styles.maintenanceNotice}>
                <h3>Website maintenance</h3>
                <p>{maintenanceMessage}</p>
              </div>
            ) : (
              <>
                <div
                  className={styles.serviceTabs}
                  role="tablist"
                  aria-label="Quick booking service type"
                >
                  {quickBookingServices.map((service) => {
                    const isSelected = service.key === selectedServiceKey;

                    return (
                      <button
                        key={service.key}
                        type="button"
                        role="tab"
                        aria-selected={isSelected}
                        className={
                          isSelected
                            ? styles.serviceTabActive
                            : styles.serviceTab
                        }
                        onClick={() => setSelectedServiceKey(service.key)}
                      >
                        <span>{service.shortTitle}</span>
                        <strong>
                          {service.pricePrefix} {formatServicePrice(service.basePrice)}
                        </strong>
                      </button>
                    );
                  })}
                </div>

                <div className={styles.serviceTabIntro}>
                  <h3>{selectedService.shortTitle}</h3>
                  <p>{selectedService.description}</p>
                </div>

                <div className={styles.formGrid}>
                  <label className={styles.field}>
                    <span>Bedrooms</span>
                    <select
                      value={bedrooms}
                      onChange={(event) =>
                        setBedrooms(Number(event.target.value))
                      }
                    >
                      {[1, 2, 3, 4, 5, 6, 7, 8].map((value) => (
                        <option key={value} value={value}>
                          {value}
                          {value === 8 ? "+" : ""}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className={styles.field}>
                    <span>Fuseboxes</span>
                    <select
                      value={fuseboxes}
                      onChange={(event) =>
                        setFuseboxes(Number(event.target.value))
                      }
                    >
                      {[1, 2, 3, 4, 5, 6].map((value) => (
                        <option key={value} value={value}>
                          {value}
                          {value === 6 ? "+" : ""}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className={`${styles.field} ${styles.fullWidth}`}>
                    <span>London postcode</span>
                    <input
                      type="text"
                      value={item.address.postcode}
                      placeholder="e.g. SW11 3AA"
                      onBlur={() => {
                        if (item.address.postcode.trim()) {
                          void checkCoverage();
                        }
                      }}
                      onChange={(event) =>
                        setItem((prevItem) => ({
                          ...prevItem,
                          address: {
                            ...prevItem.address,
                            postcode: capitaliseAllChars(event.target.value),
                          },
                        }))
                      }
                    />
                  </label>

                  <label className={styles.field}>
                    <span>Preferred date</span>
                    <DatePicker
                      selected={preferredDate}
                      onChange={(date) => setPreferredDate(date)}
                      dateFormat="dd/MM/yyyy"
                      placeholderText="dd/mm/yyyy"
                      filterDate={isWeekday}
                      minDate={defaultBookingDate}
                      maxDate={addDays(new Date(), 120)}
                      showPopperArrow={false}
                      onFocus={(event) => {
                        event.target.readOnly = true;
                      }}
                      className={styles.datePickerInput}
                    />
                  </label>

                  <label className={styles.field}>
                    <span>Time</span>
                    <select
                      value={selectedTime}
                      onChange={(event) => setSelectedTime(event.target.value)}
                    >
                      {timeSlots.map((slot) => (
                        <option key={slot.timeSlot} value={slot.timeSlot}>
                          {slot.label}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>

                <div className={styles.pricePanel}>
                  <div>
                    <span className={styles.priceCaption}>Price</span>
                    <div className={styles.priceValue}>{formatServicePrice(item.price)}</div>
                  </div>
                  <p className={styles.availabilityMessage}>
                    {availabilityMessage}
                  </p>
                </div>

                <button
                  type="button"
                  className={styles.ctaButton}
                  onClick={() => void handleContinueToBooking()}
                  disabled={checkingCoverage}
                >
                  {checkingCoverage
                    ? "Checking availability..."
                    : "Continue to secure booking →"}
                </button>
                <p className={styles.securityNote}>
                  🔒 Your details are kept private and secure
                </p>
              </>
            )}
          </aside>
        </div>
      </section>

      <section className={styles.statsBar}>
        <div>
          <strong>From £79 or £250</strong>
          <span>Standard and guaranteed pass options</span>
        </div>
        <div>
          <strong>London-wide</strong>
          <span>Convenient local coverage</span>
        </div>
        <div>
          <strong>Digital report</strong>
          <span>Easy to store and share</span>
        </div>
        <div>
          <strong>Fast response</strong>
          <span>Typically within 1 hour</span>
        </div>
      </section>

      {showSecureBooking && !maintenanceMode && (
        <section className={styles.secureBooking} ref={secureBookingRef}>
          <div className={styles.secureBookingHeader}>
            <span className={styles.sectionEyebrow}>Secure booking</span>
            <h2>Complete your details</h2>
            <p>
              We have your property size, postcode and appointment slot. Add the
              booking contact and property address below, then continue to
              secure payment.
            </p>
          </div>

          <div className={styles.secureBookingGrid}>
            <div className={styles.detailsPanel}>
              <h3>Booking contact</h3>
              <div className={styles.inputGrid}>
                <label className={styles.field}>
                  <span>Full name</span>
                  <input
                    type="text"
                    value={item.customerName}
                    onChange={(event) =>
                      setItem((prevItem) => ({
                        ...prevItem,
                        customerName: capitaliseFirstLetter(event.target.value),
                      }))
                    }
                  />
                </label>

                <label className={styles.field}>
                  <span>Email</span>
                  <input
                    type="email"
                    value={item.customerEmail}
                    onChange={(event) =>
                      setItem((prevItem) => ({
                        ...prevItem,
                        customerEmail: event.target.value,
                      }))
                    }
                  />
                </label>

                <label className={styles.field}>
                  <span>Phone number</span>
                  <input
                    type="tel"
                    value={item.customerNumber}
                    onChange={(event) =>
                      setItem((prevItem) => ({
                        ...prevItem,
                        customerNumber: event.target.value,
                      }))
                    }
                  />
                </label>
              </div>

              <h3>Property address</h3>
              <div className={styles.inputGrid}>
                <label className={`${styles.field} ${styles.fullWidth}`}>
                  <span>Find address</span>
                  <div className={styles.lookupInputWrap}>
                    <input
                      type="text"
                      value={addressLookupQuery}
                      placeholder="Start typing the postcode or address"
                      onChange={(event) => {
                        const nextValue = event.target.value;
                        setAddressLookupQuery(nextValue);
                        void findPostcode(nextValue);
                      }}
                    />
                    {addressLookupResults.length > 0 && (
                      <div className={styles.lookupResults}>
                        {addressLookupResults.map((result, index) => (
                          <button
                            key={`${result.id || result.summaryline || "result"}-${index}`}
                            type="button"
                            className={styles.lookupResult}
                            onClick={() =>
                              void handleAddressLookupSelect(result)
                            }
                          >
                            <FaMapMarkerAlt
                              className={styles.lookupResultIcon}
                            />
                            <span>
                              {result.summaryline ? (
                                <strong>{result.summaryline}</strong>
                              ) : null}
                              {result.locationsummary
                                ? `, ${result.locationsummary}`
                                : ""}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </label>

                <label className={`${styles.field} ${styles.fullWidth}`}>
                  <span>Address line 1</span>
                  <input
                    type="text"
                    value={item.address.line1}
                    onChange={(event) =>
                      setItem((prevItem) => ({
                        ...prevItem,
                        address: {
                          ...prevItem.address,
                          line1: capitaliseFirstLetter(event.target.value),
                        },
                      }))
                    }
                  />
                </label>

                <label className={`${styles.field} ${styles.fullWidth}`}>
                  <span>Address line 2</span>
                  <input
                    type="text"
                    value={item.address.line2 === " " ? "" : item.address.line2}
                    onChange={(event) =>
                      setItem((prevItem) => ({
                        ...prevItem,
                        address: {
                          ...prevItem.address,
                          line2: event.target.value
                            ? capitaliseFirstLetter(event.target.value)
                            : " ",
                        },
                      }))
                    }
                  />
                </label>

                <label className={styles.field}>
                  <span>Town or city</span>
                  <input
                    type="text"
                    value={item.address.postTown}
                    onChange={(event) =>
                      setItem((prevItem) => ({
                        ...prevItem,
                        address: {
                          ...prevItem.address,
                          postTown: capitaliseFirstLetter(event.target.value),
                        },
                      }))
                    }
                  />
                </label>

                <label className={styles.field}>
                  <span>Postcode</span>
                  <input
                    type="text"
                    value={item.address.postcode}
                    onBlur={() => {
                      if (item.address.postcode.trim()) {
                        void checkCoverage();
                      }
                    }}
                    onChange={(event) =>
                      setItem((prevItem) => ({
                        ...prevItem,
                        address: {
                          ...prevItem.address,
                          postcode: capitaliseAllChars(event.target.value),
                        },
                      }))
                    }
                  />
                </label>
              </div>

              <h3>Property access</h3>
              <div className={styles.accessSwitches}>
                <button
                  type="button"
                  className={
                    !accessProvidedByOther
                      ? styles.accessOptionActive
                      : styles.accessOption
                  }
                  onClick={() => {
                    setAccessProvidedByOther(false);
                    setItem((prevItem) => ({
                      ...prevItem,
                      access: "Access provided by: customer",
                    }));
                  }}
                >
                  I will provide access
                </button>
                <button
                  type="button"
                  className={
                    accessProvidedByOther
                      ? styles.accessOptionActive
                      : styles.accessOption
                  }
                  onClick={() => {
                    setAccessProvidedByOther(true);
                    setItem((prevItem) => ({
                      ...prevItem,
                      access: `Access provided by: Name: ${prevItem.accessName}; Email: ${prevItem.accessEmail}; Number: ${prevItem.accessNumber}`,
                    }));
                  }}
                >
                  Someone else will provide access
                </button>
              </div>

              {accessProvidedByOther && (
                <div className={styles.inputGrid}>
                  <label className={styles.field}>
                    <span>Access contact name</span>
                    <input
                      type="text"
                      value={item.accessName}
                      onChange={(event) =>
                        setItem((prevItem) => {
                          const nextName = capitaliseFirstLetter(
                            event.target.value,
                          );
                          return {
                            ...prevItem,
                            accessName: nextName,
                            access: `Access provided by: Name: ${nextName}; Email: ${prevItem.accessEmail}; Number: ${prevItem.accessNumber}`,
                          };
                        })
                      }
                    />
                  </label>

                  <label className={styles.field}>
                    <span>Access contact email</span>
                    <input
                      type="email"
                      value={item.accessEmail}
                      onChange={(event) =>
                        setItem((prevItem) => ({
                          ...prevItem,
                          accessEmail: event.target.value,
                          access: `Access provided by: Name: ${prevItem.accessName}; Email: ${event.target.value}; Number: ${prevItem.accessNumber}`,
                        }))
                      }
                    />
                  </label>

                  <label className={styles.field}>
                    <span>Access contact phone</span>
                    <input
                      type="tel"
                      value={item.accessNumber}
                      onChange={(event) =>
                        setItem((prevItem) => ({
                          ...prevItem,
                          accessNumber: event.target.value,
                          access: `Access provided by: Name: ${prevItem.accessName}; Email: ${prevItem.accessEmail}; Number: ${event.target.value}`,
                        }))
                      }
                    />
                  </label>
                </div>
              )}
            </div>

            <div className={styles.summaryPanel}>
              <h3>Your appointment</h3>
              <div className={styles.summaryRow}>
                <span>Service</span>
                <strong>{selectedService.shortTitle}</strong>
              </div>
              <div className={styles.summaryRow}>
                <span>Property</span>
                <strong>
                  {bedrooms} bedroom{bedrooms > 1 ? "s" : ""}, {fuseboxes}{" "}
                  fusebox{fuseboxes > 1 ? "es" : ""}
                </strong>
              </div>
              <div className={styles.summaryRow}>
                <span>Date</span>
                <strong>{item.emailDate}</strong>
              </div>
              <div className={styles.summaryRow}>
                <span>Time</span>
                <strong>{appointmentTime}</strong>
              </div>
              <div className={styles.summaryRow}>
                <span>Address</span>
                <strong>
                  {item.address.postcode || "Add the property postcode"}
                </strong>
              </div>
              <div className={styles.summaryDivider} />
              <div className={styles.summaryRow}>
                <span>Price</span>
                <strong className={styles.totalPrice}>{formatServicePrice(item.price)}</strong>
              </div>
              <p className={styles.summaryNote}>
                Final price shown. You will review the details once more before being redirected to
                Stripe's secure checkout.
              </p>
              <button
                type="button"
                className={styles.ctaButton}
                onClick={() => void handleOpenSummary()}
              >
                Continue to payment
              </button>
            </div>
          </div>
        </section>
      )}

      {error && <p className={styles.errorMessage}>{error}</p>}

      <section className={styles.stepsSection}>
        <div className={styles.sectionIntro}>
          <span className={styles.sectionEyebrow}>
            Made simple for landlords
          </span>
          <h2>Your EICR sorted in three straightforward steps</h2>
          <p>
            Book around your tenant or agent, then receive a clear record of the
            property's electrical condition.
          </p>
        </div>
        <div className={styles.stepsGrid}>
          <article className={styles.stepCard}>
            <div className={styles.stepTop}>
              <div className={styles.stepNumber}>01</div>
              <span className={styles.stepIcon}>⌂</span>
            </div>
            <h3>Choose your appointment</h3>
            <p>
              Enter the property details and select a morning or afternoon slot.
            </p>
          </article>
          <article className={styles.stepCard}>
            <div className={styles.stepTop}>
              <div className={styles.stepNumber}>02</div>
              <span className={styles.stepIcon}>⚡</span>
            </div>
            <h3>We inspect the electrics</h3>
            <p>
              Wiring, sockets, switches and the consumer unit are assessed for
              safety.
            </p>
          </article>
          <article className={styles.stepCard}>
            <div className={styles.stepTop}>
              <div className={styles.stepNumber}>03</div>
              <span className={styles.stepIcon}>✓</span>
            </div>
            <h3>Receive your report</h3>
            <p>
              Get your digital EICR with any observations and recommended next
              steps.
            </p>
          </article>
        </div>
      </section>

      <section className={styles.guaranteedSection}>
        <div className={styles.guaranteedIntro}>
          <span className={styles.sectionEyebrow}>Guaranteed pass option</span>
          <h2>Want the simplest route to a satisfactory EICR?</h2>
          <p>
            Our guaranteed pass EICR is built for landlords who want cost
            certainty and a straightforward route to compliance.
          </p>
        </div>

        <div className={styles.guaranteedGrid}>
          <article className={styles.guaranteedCardPrimary}>
              <div className={styles.guaranteedPrice}>Fixed price £250</div>
            <p>
              Your inspection is carried out as normal, and if minor remedial
              items are needed to achieve a satisfactory result, we include
              them within this service.
            </p>
            <ul className={styles.guaranteedList}>
              <li>Full EICR inspection and testing</li>
              <li>Minor remedial works included where required for a pass</li>
              <li>Clear pricing before you book</li>
              <li>Ideal for landlords who want fewer follow-up steps</li>
            </ul>
            <a href="#booking-card" className={styles.inlineCta}>
              Quick book guaranteed pass <span aria-hidden="true">→</span>
            </a>
          </article>

          <article className={styles.guaranteedCardSecondary}>
            <h3>What is and is not included</h3>
            <div className={styles.guaranteedDetailGroup}>
              <strong>Included</strong>
              <p>
                Minor wiring repairs, replacement accessories, bonding
                corrections and small adjustments identified during testing.
              </p>
            </div>
            <div className={styles.guaranteedDetailGroup}>
              <strong>Not included</strong>
              <p>
                Major works such as full or partial rewires, consumer unit
                replacement, or extensive circuit alterations and upgrades.
              </p>
            </div>
            <div className={styles.guaranteedDetailGroup}>
              <strong>Additional option</strong>
              <p>
                If a consumer unit replacement is required, this can be carried
                out for an additional fixed cost of £450.
              </p>
            </div>
          </article>
        </div>
      </section>

      <section className={styles.valueSection}>
        <div className={styles.valueCopy}>
          <span className={styles.sectionEyebrow}>Know where you stand</span>
          <h2>Protect your tenants, your property and your peace of mind.</h2>
          <p>
            An EICR identifies damage, deterioration and defects that could put
            people or the property at risk. Teck Engineers gives you a clear,
            practical report—not confusing jargon.
          </p>
          <a href="#booking-card" className={styles.inlineCta}>
            Book your inspection <span aria-hidden="true">→</span>
          </a>
        </div>
        <div
          className={styles.inspectionCard}
          aria-label="Electrical installation inspection summary"
        >
          <div className={styles.inspectionCardTop}>
            <span>TECK ENGINEERS</span>
            <span>EICR</span>
          </div>
          <div className={styles.inspectionStatus}>
            <span>Electrical installation</span>
            <strong>INSPECTED</strong>
          </div>
          <ul className={styles.checkList}>
            <li>Wiring &amp; fixed electrics</li>
            <li>Sockets &amp; switches</li>
            <li>Consumer unit / fusebox</li>
            <li>Earthing &amp; bonding</li>
          </ul>
        </div>
      </section>

      <section className={styles.faqSection}>
        <div className={styles.sectionIntro}>
          <span className={styles.sectionEyebrow}>Useful to know</span>
          <h2>EICR questions, answered</h2>
        </div>
        <div className={styles.faqList}>
          {faqs.map((faq) => (
            <details key={faq.question} className={styles.faqItem}>
              <summary>{faq.question}</summary>
              <p>{faq.answer}</p>
            </details>
          ))}
        </div>
      </section>

      <section className={styles.finalCta}>
        <h2>Ready to get your EICR booked?</h2>
        <p>Check an appointment and estimated price in under a minute.</p>
        <div className={styles.finalActions}>
          <a href="#booking-card" className={styles.primaryAction}>
            Check availability <span aria-hidden="true">→</span>
          </a>
        </div>
      </section>

      <footer className={styles.footer}>
        <div className={styles.footerInner}>
          <div className={styles.footerBrand}>
            <img
              src={logo}
              alt="Teck Engineers"
              className={styles.footerLogo}
            />
            <p>
              Electrical inspection and certification for London properties.
            </p>
          </div>
          <div className={styles.footerContact}>
            <a href="tel:07496445148">07496 445 148</a>
            <a href="mailto:info@teckengineers.co.uk">
              info@teckengineers.co.uk
            </a>
          </div>
        </div>
        <div className={styles.footerBottom}>© 2026 Teck Engineers</div>
      </footer>

      <div className={styles.mobileSticky}>
        <a href="tel:07496445148" className={styles.mobileCall}>
          Call now
        </a>
        <a href="#booking-card" className={styles.mobileBook}>
          Book inspection →
        </a>
      </div>

      <SummaryModal
        show={showSummary}
        handleClose={() => setShowSummary(false)}
        item={item}
        setError={setError}
        serviceRequired="EICR"
      />
    </div>
  );
}
